/**
 * packages/shared/src/scoring/damage-budget.ts
 *
 * "Reliability dossier / damage budget" (RUF-554, design D7 —
 * docs/prds/digital-passport-health-confidence.md §3). Rejected inventing a
 * "% of factory spare rows consumed" denominator — no reliable per-SKU
 * factory-max exists. Adopted instead: lifetime cumulative counts (same
 * lifetime-not-window philosophy as telemetry-disclosures.ts — an incident months
 * outside every scoring window must still show up) anchored VERBATIM to the
 * v5 model's own goodThreshold/criticalThreshold for that telemetryName
 * (models/telemetry-scoring.v5.ts) — no invented numbers.
 *
 * Metric list mirrors the PRD's Reliability Dossier section:
 *   - HBM: row remaps, single/double-bit retired pages.
 *   - Thermal history: shutdown events, throttle events, max observed temp.
 *   - Bus/fabric: PCIe link-downgrade, NVLink link-down, fallen-off-bus.
 */

import { telemetryScoringModel } from './models/telemetry-scoring.v5.js';
import type { DailyDisclosureInputs } from './telemetry-disclosures.js';

export type DamageBudgetAggregate = 'sum' | 'max';

export interface DamageBudgetMetric {
  /** Stable machine key for the client — snake_case, matches DTO field naming source. */
  key: string;
  /** Short human label for display. */
  label: string;
  /** v5 model telemetryName this metric is sourced/thresholded from. */
  telemetryName: string;
  /** Lifetime cumulative value (sum of daily events, or max of a daily gauge). */
  value: number;
  /** v5 model's own goodThreshold for this telemetryName. */
  goodThreshold: number;
  /** v5 model's own criticalThreshold for this telemetryName. */
  criticalThreshold: number;
}

interface MetricDef {
  key: string;
  label: string;
  telemetryName: string;
  aggregate: DamageBudgetAggregate;
}

const METRIC_DEFS: readonly MetricDef[] = [
  { key: 'row_remap_count', label: 'Row remaps', telemetryName: 'Row_Remap_Count', aggregate: 'sum' },
  {
    key: 'retired_pages_single_bit',
    label: 'Retired pages (single-bit)',
    telemetryName: 'Retired_Pages_Single_Bit',
    aggregate: 'sum',
  },
  {
    key: 'retired_pages_double_bit',
    label: 'Retired pages (double-bit)',
    telemetryName: 'Retired_Pages_Double_Bit',
    aggregate: 'sum',
  },
  {
    key: 'thermal_shutdown_event_count',
    label: 'Thermal shutdown events',
    telemetryName: 'Thermal_Shutdown_Event_Count',
    aggregate: 'sum',
  },
  {
    key: 'gpu_thermal_throttle_count',
    label: 'Thermal throttle events',
    telemetryName: 'GPU_Thermal_Throttle_Count',
    aggregate: 'sum',
  },
  {
    key: 'gpu_temperature_max_observed_c',
    label: 'Max observed GPU temperature (C)',
    telemetryName: 'GPU_Temperature_Max_Observed_C',
    aggregate: 'max',
  },
  {
    key: 'pcie_link_downgrade_count',
    label: 'PCIe link-downgrade events',
    telemetryName: 'PCIe_Link_Downgrade_Flag',
    aggregate: 'sum',
  },
  {
    key: 'gpu_fallen_off_bus_count',
    label: 'GPU fallen-off-bus events',
    telemetryName: 'GPU_Fallen_Off_Bus_Count',
    aggregate: 'sum',
  },
  {
    key: 'nvlink_link_down_count',
    label: 'NVLink link-down events',
    telemetryName: 'NVLink_Link_Down_Count',
    aggregate: 'sum',
  },
];

const RULE_BY_NAME = new Map(telemetryScoringModel.rules.map((r) => [r.telemetryName, r]));

/**
 * Compute the passport's damage-budget metric list from an asset's FULL
 * lifetime telemetry_daily_agg history (same `{day, inputs}[]` shape
 * telemetry-disclosures.ts's aggregateLifetimeDisclosureInputs consumes — callers map
 * telemetry_daily_agg rows via mapDailyAggToTelemetryInputs once and reuse
 * the result for both disclosures and this).
 *
 * A metric with no v5 model rule for its telemetryName (model drift) is
 * skipped rather than guessing thresholds. A metric with no observed reading
 * across the lifetime history is still emitted with value 0 (no evidence of
 * damage, not "unknown") — mirrors telemetry-disclosures.ts's `?? 0` defaulting.
 */
export function computeDamageBudget(daily: readonly DailyDisclosureInputs[]): DamageBudgetMetric[] {
  const sums = new Map<string, number>();
  const maxes = new Map<string, number>();

  for (const { inputs } of daily) {
    for (const { Telemetry_Name: name, Value_Num: value } of inputs) {
      if (!Number.isFinite(value)) continue;
      sums.set(name, (sums.get(name) ?? 0) + value);
      maxes.set(name, Math.max(maxes.get(name) ?? -Infinity, value));
    }
  }

  const out: DamageBudgetMetric[] = [];
  for (const def of METRIC_DEFS) {
    const rule = RULE_BY_NAME.get(def.telemetryName);
    if (!rule) continue; // model drift — no thresholds to anchor to, skip rather than guess

    const raw = def.aggregate === 'sum' ? (sums.get(def.telemetryName) ?? 0) : maxes.get(def.telemetryName);
    const value = raw === undefined || raw === -Infinity ? 0 : raw;

    out.push({
      key: def.key,
      label: def.label,
      telemetryName: def.telemetryName,
      value,
      goodThreshold: rule.goodThreshold,
      criticalThreshold: rule.criticalThreshold,
    });
  }
  return out;
}
